import useCheckUnsavedChanges from 'core/custom-hooks/use-check-unsaved-changes.hook';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { initialPredefinedCommentFormModel } from '../settings-devices-predefined-comments-form.constants';
import {
    PredefinedCommentDeviceTypeEntry,
    PredefinedCommentFormModel,
} from '../settings-devices-predefined-comments-form.model';
import { usePredefinedCommentValidation } from 'modules/settings/pods/settings-devices/settings-devices-predefined-comments/settings-devices-predefined-comments-form/hooks/use-predefined-comment-validation.hook';
import { usePredefinedCommentFormContext } from 'modules/settings/pods/settings-devices/settings-devices-predefined-comments/settings-devices-predefined-comments-form/hooks/use-predefined-comment-form-context.hook';
import { useNavigate } from 'react-router-dom';
import { isCommentPriorityValid } from '../settings-devices-predefined-comments-form.validation';

export const usePredefinedCommentDataHandler = () => {
    const { validity } = usePredefinedCommentValidation();
    const { predefinedComment } = usePredefinedCommentFormContext();

    const initialDataRef = useRef<PredefinedCommentFormModel>(initialPredefinedCommentFormModel);
    const isInitializedRef = useRef(false);
    const navigate = useNavigate();
    const [formData, setFormData] = useState<PredefinedCommentFormModel>(
        predefinedComment ? structuredClone(predefinedComment) : initialPredefinedCommentFormModel
    );

    const handleOnClose = useCallback(() => {
        const currentPath = location.pathname;
        const pathSegments = currentPath.split('/');
        const newPath = pathSegments.slice(0, -1).join('/');
        navigate(newPath);
    }, [navigate]);

    const unsavedData = useCheckUnsavedChanges(handleOnClose);

    const compareDataChanged = (data: PredefinedCommentFormModel): boolean => {
        if (!isInitializedRef.current) {
            return false;
        }
        return JSON.stringify(data) !== JSON.stringify(initialDataRef.current);
    };

    // If the predefined comment with given ID exists, then override the standard form data.
    useEffect(() => {
        if (!predefinedComment) {
            isInitializedRef.current = true;
            return;
        }

        const newComment = structuredClone(predefinedComment);
        setFormData(newComment);
        initialDataRef.current = newComment;
        isInitializedRef.current = true;
    }, [predefinedComment]);

    const handleDataChange = (field: keyof PredefinedCommentFormModel, value: string | boolean | null) => {
        setFormData((prev) => {
            const newState = { ...prev, [field]: value };
            unsavedData.setIsUnsavedChanges(compareDataChanged(newState));
            return newState;
        });
    };

    const handleAddDeviceTypeEntry = () => {
        setFormData((prev) => {
            const newEntry: PredefinedCommentDeviceTypeEntry = {
                id: crypto.randomUUID(),
                deviceTypeId: undefined,
                resultTypes: [],
                erroneous: false,
                locations: [],
                order: null,
            };
            const newState = { ...prev, deviceTypeEntries: [...prev.deviceTypeEntries, newEntry] };
            unsavedData.setIsUnsavedChanges(compareDataChanged(newState));
            return newState;
        });
    };

    const handleRemoveDeviceTypeEntry = (entryId: string) => {
        setFormData((prev) => {
            if (prev.deviceTypeEntries.length <= 1) {
                return prev;
            }
            const newState = {
                ...prev,
                deviceTypeEntries: prev.deviceTypeEntries.filter((entry) => entry.id !== entryId),
            };
            unsavedData.setIsUnsavedChanges(compareDataChanged(newState));
            return newState;
        });
    };

    const handleEntryChange = <K extends keyof PredefinedCommentDeviceTypeEntry>(
        entryId: string,
        field: K,
        value: PredefinedCommentDeviceTypeEntry[K]
    ) => {
        setFormData((prev) => {
            const newState = {
                ...prev,
                deviceTypeEntries: prev.deviceTypeEntries.map((entry) =>
                    entry.id === entryId ? { ...entry, [field]: value } : entry
                ),
            };
            unsavedData.setIsUnsavedChanges(compareDataChanged(newState));
            return newState;
        });
    };

    // Memoize whether form data has changed from initial state
    const hasDataChanged = useMemo(() => compareDataChanged(formData), [formData]);

    const isDataValid = (): boolean => {
        const hasCommentText = formData.commentText?.trim().length > 0;
        const allDeviceTypesSelected = formData.deviceTypeEntries.every((entry) => entry.deviceTypeId);
        const allDeviceTypesAtLeastOneResultTypeSelected = formData.deviceTypeEntries.every(
            (entry) => entry.resultTypes.length > 0
        );
        const hasAtLeastOneLocation = formData.deviceTypeEntries.every((entry) => entry.locations.length > 0);
        const allOrdersWithinRange = formData.deviceTypeEntries.every((entry) => isCommentPriorityValid(entry.order));
        const isFormDataValid = Object.values(validity).every((e) => e.state === 'valid');
        return (
            hasDataChanged &&
            hasCommentText &&
            allDeviceTypesSelected &&
            allDeviceTypesAtLeastOneResultTypeSelected &&
            isFormDataValid &&
            hasAtLeastOneLocation &&
            allOrdersWithinRange
        );
    };

    return {
        formData,
        handleDataChange,
        handleAddDeviceTypeEntry,
        handleRemoveDeviceTypeEntry,
        handleEntryChange,
        unsavedData,
        isDataValid,
        handleOnClose,
    };
};
